/**
 * ContractorResetPassword — set new password from email reset link.
 * Route: /reset-password?reset=TOKEN
 */

import { useState, FormEvent } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import PasswordInput from '../../components/auth/PasswordInput'
import { apiFetch } from '../../api/client'

export default function ContractorResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('reset') || ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!token) {
      setError('Invalid or missing reset link. Request a new one from the login page.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    setSubmitting(true)
    try {
      await apiFetch('POST', '/api/auth/reset-password', { token, password })
      navigate('/login?reset=success', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 380,
        background: '#fff',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r)',
        padding: '28px 24px',
      }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', margin: '0 0 6px' }}>
          Set new password
        </h1>
        <p style={{ fontSize: 12, color: 'var(--ink4)', margin: '0 0 22px', lineHeight: 1.5 }}>
          Choose a new password for your account.
        </p>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', marginBottom: 14 }}>
            <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 6 }}>
              New password
            </span>
            <PasswordInput
              id="reset-password"
              name="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{ padding: '10px 12px', fontSize: 14 }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: 18 }}>
            <span style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 6 }}>
              Confirm password
            </span>
            <PasswordInput
              id="reset-password-confirm"
              name="passwordConfirm"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              style={{ padding: '10px 12px', fontSize: 14 }}
            />
          </label>

          {error && (
            <p style={{ fontSize: 12, color: 'var(--red)', margin: '0 0 14px' }} role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="btn b-dk"
            disabled={submitting}
            style={{ width: '100%', justifyContent: 'center', padding: '10px 16px', fontWeight: 600 }}
          >
            {submitting ? 'Saving…' : 'Set password'}
          </button>
        </form>

        <p style={{ fontSize: 12, color: 'var(--ink4)', margin: '18px 0 0', textAlign: 'center' }}>
          <Link to="/login" style={{ color: 'var(--ink3)', textDecoration: 'none', fontWeight: 600 }}>
            ← Back to sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
